import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";

import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

import PageLayout from "examples/LayoutContainers/PageLayout";
import LanguageSelector from "examples/Navbars/CustomNavbar/LanguageSelector";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import config from "../../../config";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function AffiliateResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query = useQuery();
  const token = query.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const isTokenMissing = !token;
  const passwordsMatch = password === confirmPassword;
  const passwordStrongEnough = password.length >= 8;

  const canSubmit =
    !isTokenMissing &&
    passwordStrongEnough &&
    passwordsMatch &&
    !loading;

  const handleSubmit = async () => {
    setErrMsg("");

    if (isTokenMissing) {
      setErrMsg(t("resetPassword.missingToken"));
      return;
    }

    if (!password || password.length < 8) {
      setErrMsg(t("resetPassword.minCharsError"));
      return;
    }

    if (password !== confirmPassword) {
      setErrMsg(t("resetPassword.mismatchError"));
      return;
    }

    setLoading(true);
    try {
      // ✅ Backend endpoint for affiliates/clients reset
      // Adjust path if you used a different one.
      await axios.post(`${config.BACKEND_BASE_URL}/api/admin/authentication/reset-password/client`, {
        token,
        password,
      });

      setSuccess(true);
    } catch (e) {
      setErrMsg(
        e?.response?.data?.message ||
        e?.response?.data ||
        t("resetPassword.invalidLink")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <MDBox
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        px={{ xs: 3, md: 5 }}
        pt={2}
      >
        <LanguageSelector />
      </MDBox>

      <MDBox minHeight="65vh" display="flex" alignItems="center" py={4}>
        <Container>
          <Grid container justifyContent="center">
            <Grid item xs={12} sm={10} md={7} lg={5}>
              <Card>
                <MDBox p={4}>
                  <MDBox textAlign="center" mb={2}>
                    <MDTypography variant="h4" fontWeight="bold">
                      {t("resetPassword.title")}
                    </MDTypography>

                    <MDTypography variant="button" color="text">
                      {t("resetPassword.subtitle")}
                    </MDTypography>
                  </MDBox>

                  {success ? (
                    <MDBox textAlign="center" mt={3}>
                      <MDBox
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="3rem"
                        height="3rem"
                        borderRadius="xl"
                        mx="auto"
                        mb={1.5}
                        bgColor="success"
                      >
                        <Icon sx={{ color: "#fff" }}>check</Icon>
                      </MDBox>

                      <MDTypography variant="h6" fontWeight="medium" mb={1}>
                        {t("resetPassword.successTitle")}
                      </MDTypography>

                      <MDTypography variant="button" color="text" mb={3} display="block">
                        {t("resetPassword.successMsg")}
                      </MDTypography>

                      <MDButton
                        variant="gradient"
                        color="info"
                        fullWidth
                        onClick={() => navigate("/authentication/sign-in")}
                      >
                        {t("resetPassword.goToSignIn")}
                      </MDButton>
                    </MDBox>
                  ) : (
                    <>
                      <MDBox mb={1}>
                        <MDInput
                          type="password"
                          label={t("resetPassword.newPassword")}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          fullWidth
                          disabled={loading}
                        />
                      </MDBox>
                      {password && password.length < 8 && (
                        <MDTypography variant="caption" color="error">
                          {t("resetPassword.minCharsHint")}
                        </MDTypography>
                      )}

                      <MDBox mb={0}>
                        <MDInput
                          type="password"
                          label={t("resetPassword.confirmPassword")}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          fullWidth
                          disabled={loading}
                        />
                      </MDBox>
                      {confirmPassword && !passwordsMatch && (
                        <MDTypography variant="caption" color="error">
                          {t("resetPassword.passwordsMismatch")}
                        </MDTypography>
                      )}

                      {errMsg ? (
                        <MDBox mb={2}>
                          <MDTypography variant="button" color="error">
                            {String(errMsg)}
                          </MDTypography>
                        </MDBox>
                      ) : null}

                      {isTokenMissing ? (
                        <MDBox mb={2}>
                          <MDTypography variant="button" color="warning">
                            {t("resetPassword.missingTokenWarning")}
                          </MDTypography>
                        </MDBox>
                      ) : null}

                      <MDBox mt={3} display="flex" alignItems="center" gap={1.5}>
                        <MDButton
                          variant="gradient"
                          color="info"
                          fullWidth
                          disabled={!canSubmit}
                          onClick={handleSubmit}
                        >
                          {loading ? (
                            <MDBox display="flex" alignItems="center" gap={1}>
                              <CircularProgress size={18} />
                              <MDTypography variant="button" color="white">
                                {t("resetPassword.saving")}
                              </MDTypography>
                            </MDBox>
                          ) : (
                            t("resetPassword.save")
                          )}
                        </MDButton>

                        <MDButton
                          variant="outlined"
                          color="dark"
                          disabled={loading}
                          onClick={() => navigate("/authentication/sign-in")}
                        >
                          {t("common.cancel")}
                        </MDButton>
                      </MDBox>

                      <MDBox mt={2}>
                        <MDTypography variant="caption" color="text">
                          {t("resetPassword.expiredNote")}
                        </MDTypography>
                      </MDBox>
                    </>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </MDBox>
    </PageLayout>
  );
}
